/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {ClampedTextWithTooltip} from '@ion/dls/src/components/assetcard/current/ClampedTextWithTooltip';
import {AssetEquipmentIcon} from '@ion/dls/src/icons/current/AssetEquipmentIcon';
import {AssetToolIcon} from '@ion/dls/src/icons/current/AssetToolIcon';
import {AssetTrailerIcon} from '@ion/dls/src/icons/current/AssetTrailerIcon';
import {AssetVehicleIcon} from '@ion/dls/src/icons/current/AssetVehicleIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {RangeStatus, RangeStatusBadge} from './RangeStatusBadge';
import {TagsTabKey} from './TagsCoachingText';

export type TagAssetType = 'tool' | 'trailer' | 'equipment' | 'vehicle';
export type TagAssignmentStatus = 'unassigned' | 'assignedToOther';

// shared with ToolCard.tsx - the same four asset types map to the same
// icons in both the compact Tag Title row and the taller Tool Card header ...
export const TAG_ASSET_TYPE_ICONS = {
  tool      : AssetToolIcon,
  trailer   : AssetTrailerIcon,
  equipment : AssetEquipmentIcon,
  vehicle   : AssetVehicleIcon
} as const;

export interface TagTitleRowProps {
  assetType: TagAssetType;
  assetName: string;
  // which Tags-accordion tab this row is rendered under - the Nearby tab
  // only ever lists tags currently in range (that's what makes them
  // "nearby"), so `range` is only meaningful (and only read) for
  // context='assigned' rows; nearby rows always render the in-range icon ...
  context: TagsTabKey;
  range: RangeStatus;
  // required when context='nearby' - per Figma's Tag Title/Nearby doc,
  // nearby rows substitute an assignment-status label ('Unassigned', or the
  // other asset's name) for the usual In Range/Out of Range text ...
  assignmentStatus?: TagAssignmentStatus;
  // required when assignmentStatus='assignedToOther' ...
  assignedToName?: string;
  // shown as a second line whenever context='assigned' && range='out' -
  // falls back to "Unknown" when not provided, rather than omitting the
  // line, since an out-of-range tag always shows this line per Figma ...
  lastKnownLocation?: string;
  onNameClick?: () => void;
}

const useStyles = makeStyles(() => ({
  row : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'flex-start',
    justifyContent  : 'center',
    width           : '100%',
    minHeight       : '44px',
    padding         : '8px',
    gap             : '8px',
    borderRadius    : '8px',
    border          : `1px solid ${dlsColor('grey7')}`,
    backgroundColor : dlsColor('white')
  },
  // Figma's Tile/Content/Item/Unassigned variant ...
  dashedRow : {
    borderStyle : 'dashed'
  },
  // Figma's Tile/Content/Item/OutofRange variant ...
  outOfRangeRow : {
    backgroundColor : 'rgba(226, 35, 26, 0.05)',
    borderColor     : 'rgba(226, 35, 26, 0.3)'
  },
  infoRow : {
    display        : 'flex',
    width          : '100%',
    minWidth       : 0,
    alignItems     : 'center',
    justifyContent : 'space-between',
    gap            : '8px'
  },
  tagInfo : {
    display    : 'flex',
    minWidth   : 0,
    alignItems : 'center',
    gap        : '8px'
  },
  nameLink : {
    display    : 'flex',
    minWidth   : 0,
    padding    : 0,
    border     : 'none',
    background : 'none',
    font       : 'inherit',
    textAlign  : 'left',
    cursor     : 'pointer'
  },
  nameLinkText : {
    minWidth      : 0,
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    fontSize      : '14px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : dlsColor('linkBlue')
  },
  lastKnown : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  }
}));

/**
 * TagTitleRow renders a single compact row in the Tags accordion's tab
 * panel content (ION-7463) - the tag's own asset-type icon, its name (always
 * a link to that tag's own Tool Card, per Figma's Tag Title doc), and a
 * right-aligned `RangeStatusBadge`. Assigned-tab rows show the tag's real
 * range status; Nearby-tab rows always show the (green) in-range icon but
 * substitute an assignment-status label for the badge text.
 *
 * ```typescript
 *
 *   import {TagTitleRow} from
 *     '@ion/dls/src/components/assetdetails/current/TagTitleRow';
 *   ...
 *
 *   <TagTitleRow assetType='tool'
 *     assetName='JacksBag'
 *     context='assigned'
 *     range='in'
 *     onNameClick={onNameClick} />
 *
 *   <TagTitleRow assetType='trailer'
 *     assetName='Trailer'
 *     context='assigned'
 *     range='out'
 *     lastKnownLocation='13505 Zachary Taylor Dr, Manasss, Virginia'
 *     onNameClick={onNameClick} />
 *
 *   <TagTitleRow assetType='equipment'
 *     assetName='Dell Laptop'
 *     context='nearby'
 *     range='in'
 *     assignmentStatus='unassigned'
 *     onNameClick={onNameClick} />
 *
 * ```
 *
 */
export const TagTitleRow = ({
  assetType,
  assetName,
  context,
  range,
  assignmentStatus,
  assignedToName,
  lastKnownLocation,
  onNameClick
}: TagTitleRowProps) => {
  const classes   = useStyles();
  const AssetIcon = TAG_ASSET_TYPE_ICONS[assetType];

  const isNearby        = context === 'nearby';
  const isUnassignedRow = isNearby && assignmentStatus === 'unassigned';
  const isOutOfRangeRow = context === 'assigned' && range === 'out';
  // per Figma, an out-of-range assigned tag always shows this line - when
  // the real last-known location isn't available, it shows "Unknown"
  // rather than omitting the line entirely ...
  const showLastKnown   = isOutOfRangeRow;

  const badgeRange = isNearby ? 'in' : range;
  const badgeLabel = isNearby
    ? (assignmentStatus === 'unassigned' ? 'Unassigned' : assignedToName)
    : undefined;

  const onNameButtonClick = useCallback(() => {
    onNameClick?.();
  }, [onNameClick]);

  return (
    <div className={classnames(classes.row, {
      [classes.dashedRow]     : isUnassignedRow,
      [classes.outOfRangeRow] : isOutOfRangeRow
    })}>
      <div className={classes.infoRow}>
        <div className={classes.tagInfo}>
          <AssetIcon size={IconSize.medium} color={dlsColor('grey3')} />
          <button type='button' className={classes.nameLink} onClick={onNameButtonClick}>
            <ClampedTextWithTooltip lines={1} className={classes.nameLinkText}>
              {assetName}
            </ClampedTextWithTooltip>
          </button>
        </div>
        <RangeStatusBadge range={badgeRange} label={badgeLabel} size={16} />
      </div>
      {showLastKnown && (
        <span className={classes.lastKnown}>
          {`Last Known: ${lastKnownLocation || 'Unknown'}`}
        </span>
      )}
    </div>
  );
};

export default memo(TagTitleRow);
